'use client'

import { useEffect, useId, useRef, useState, type ReactNode } from 'react'

import { CdekLogo, YandexDeliveryLogo } from '@/components/brand/marketplace-logos'
import { StorefrontMark, storefrontRegistry } from '@/components/warehouse/storefronts'

export type OrderCarrier = 'cdek' | 'yandex'

export interface OrderCardFact {
  label: string
  value?: string
}

const CARRIERS: Record<OrderCarrier, { title: string; label: string; Logo: typeof CdekLogo }> = {
  cdek: { title: 'Везёт СДЭК', label: 'СДЭК', Logo: CdekLogo },
  yandex: { title: 'Везёт Яндекс Доставка', label: 'Яндекс Доставка', Logo: YandexDeliveryLogo },
}

/** Where the order was sold and who carries it: two small logos on a quiet frosted pill. */
export function OrderChannelBadges({ carrier }: { carrier: OrderCarrier }) {
  const { title, label, Logo } = CARRIERS[carrier]
  return (
    <span className="pointer-events-auto flex h-6 items-center gap-1 rounded-full bg-paper/65 px-1 opacity-90 backdrop-blur-sm">
      <span title="Продажа на Яндекс Маркете" className="flex">
        <StorefrontMark providerCode="ymarket" />
        <span className="sr-only">{storefrontRegistry.ymarket.label}</span>
      </span>
      <span title={title} className="flex">
        <Logo size={16} />
        <span className="sr-only">{label}</span>
      </span>
    </span>
  )
}

/** The round control that floats on a card's photo. */
export function OrderCardIconButton({
  label,
  title,
  onClick,
  disabled = false,
  revealOnHover = false,
  expanded,
  controls,
  children,
}: {
  label: string
  title: string
  onClick: () => void
  disabled?: boolean
  revealOnHover?: boolean
  expanded?: boolean
  controls?: string
  children: ReactNode
}) {
  const reveal = revealOnHover && !expanded
    ? 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100'
    : ''
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-expanded={expanded}
      aria-controls={controls}
      title={title}
      className={`pointer-events-auto grid size-8 place-items-center rounded-full bg-paper/95 text-ink-2 shadow-sm ring-1 ring-rule transition hover:text-gold-deep hover:ring-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold disabled:cursor-wait motion-reduce:transition-none ${expanded ? 'text-gold-deep ring-gold' : ''} ${reveal}`}
    >
      {children}
    </button>
  )
}

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </svg>
  )
}

/** Order identifiers behind a ⋯ button, each copied with one press, and the order itself. */
export function OrderCardMenu({
  orderId,
  facts,
  onOpen,
}: {
  orderId: number
  facts: OrderCardFact[]
  onOpen: () => void
}) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState<number | null>(null)
  const root = useRef<HTMLDivElement>(null)
  const panelId = useId()

  useEffect(() => {
    if (!open) return
    const dismiss = (event: PointerEvent) => {
      if (!(event.target instanceof Node) || !root.current?.contains(event.target)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', dismiss)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('pointerdown', dismiss)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  useEffect(() => {
    if (copied === null) return
    const timer = window.setTimeout(() => setCopied(null), 1500)
    return () => window.clearTimeout(timer)
  }, [copied])

  const copy = async (index: number, value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(index)
    } catch {
      setCopied(null)
    }
  }

  return (
    <div ref={root} className="relative">
      <OrderCardIconButton
        label={`Идентификаторы заказа №${orderId}`}
        title="Идентификаторы заказа"
        onClick={() => setOpen((current) => !current)}
        expanded={open}
        controls={panelId}
        revealOnHover
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" />
        </svg>
      </OrderCardIconButton>
      <div
        id={panelId}
        hidden={!open}
        className="pointer-events-auto absolute right-0 top-10 w-64 max-w-[calc(100vw-2rem)] rounded-xl border border-rule bg-paper p-1.5 text-left shadow-[0_18px_40px_-18px_rgba(40,28,8,0.45)]"
      >
        {facts.map((fact, index) => {
          if (fact.value === undefined) {
            return <div key={`${fact.label}-${index}`} role="note" className="px-2.5 py-1.5 text-[11px] leading-snug text-ink-muted">{fact.label}</div>
          }
          const value = fact.value
          return (
            <div key={`${fact.label}-${index}`} className="px-2.5 py-1.5">
              <div className="text-[9px] font-semibold uppercase tracking-[0.1em] text-ink-faint">{fact.label}</div>
              <div className="mt-0.5 flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate font-mono text-[11.5px] text-ink-2" title={value}>{value}</span>
                <button
                  type="button"
                  onClick={() => void copy(index, value)}
                  aria-label={`Скопировать: ${fact.label} ${value}`}
                  title="Скопировать"
                  className={`grid size-6 shrink-0 place-items-center rounded-md transition-colors hover:bg-bone/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold motion-reduce:transition-none ${copied === index ? 'text-gold-deep' : 'text-ink-muted hover:text-gold-deep'}`}
                >
                  {copied === index ? <CheckIcon /> : <CopyIcon />}
                </button>
              </div>
            </div>
          )
        })}
        <span className="sr-only" aria-live="polite">{copied === null ? '' : 'Скопировано'}</span>
        <div className="mx-1 my-1 h-px bg-rule" />
        <button
          type="button"
          onClick={() => { setOpen(false); onOpen() }}
          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[12px] font-medium text-ink-2 transition-colors hover:bg-bone/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gold motion-reduce:transition-none"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
          </svg>
          Открыть заказ
        </button>
      </div>
    </div>
  )
}
